/**
 * Overview-only buyer-facing labels. Condition names come from the shared catalog
 * used by Investigate and Safety Management.
 */

export {
  FORKLIFT_PEDESTRIAN_LABEL as OVERVIEW_V2_FORKLIFT_PEDESTRIAN_LABEL,
  RESTRICTED_AREA_LABEL as OVERVIEW_V2_RESTRICTED_AREA_LABEL,
  DEMONSTRATION_CONTROL_LABEL as OVERVIEW_V2_DEMONSTRATION_CONTROL_LABEL,
  buyerConditionDisplayName as overviewV2ConditionDisplayName,
  buyerConditionSubtitle as overviewV2ConditionSubtitle,
  buyerControlDisplayName as overviewV2ControlDisplayName,
} from '../domain/buyerFacing/conditionDisplayNames';

export const OVERVIEW_V2_CURRENT_WINDOW_HEADING = 'Current 15-min window';
export const OVERVIEW_V2_PREVIOUS_WINDOW_HEADING = 'Previous 15-min window';
export const OVERVIEW_V2_EMPTY_CONTROL_DETAIL =
  'Open the Safety Case to record an action and begin follow-up measurement.';
export const OVERVIEW_V2_OPEN_SAFETY_CASE_LABEL = 'Open Safety Case';

function text(value: unknown): string {
  return String(value || '').trim();
}

export function overviewV2BannerActionItems<T extends { source?: string | null }>(items: T[]): T[] {
  return items.filter((item) => {
    const source = text(item.source);
    // Instrumentation-only: current activity and coverage/usable-time never drive the
    // first-page Action required banner. Person-owned safety work still does.
    return source !== 'current_condition' && source !== 'coverage';
  });
}

export function overviewV2CurrentActivityCount(
  items: Array<{ source?: string | null }>,
): number {
  return items.filter((item) => item.source === 'current_condition').length;
}

export function overviewV2CurrentActivitySummaryDetail(count: number): string {
  if (count <= 0) return 'No current activity';
  return count === 1 ? 'condition with activity' : 'conditions with activity';
}

export function overviewV2OccurrenceHeadline(count: number): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'occurrence' : 'occurrences'}`;
}

export function overviewV2RecurringDenominator(args: {
  occurrences?: number | null;
  usableHours?: number | null;
}): string | null {
  if (typeof args.occurrences !== 'number' || !Number.isFinite(args.occurrences)) return null;
  const count = `${args.occurrences.toLocaleString('en-US')} occurrence${args.occurrences === 1 ? '' : 's'}`;
  if (typeof args.usableHours === 'number' && Number.isFinite(args.usableHours) && args.usableHours > 0) {
    const hours = Number.isInteger(args.usableHours) ? String(args.usableHours) : args.usableHours.toFixed(1);
    return `${count} · ${hours} usable hour${args.usableHours === 1 ? '' : 's'}`;
  }
  return count;
}
